import React, { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react';

export interface ErrorDetails {
  status: number | undefined;
  errorMessage: string | undefined;
  operationName: string | undefined;
}

export interface ErrorContextType {
  errorDetails: ErrorDetails;
  setError: (status?: number, message?: string, operationName?: string | null) => void;
  clearError: () => void;
}

type ErrorActions = {
  setError: (status?: number, message?: string, operationName?: string | null) => void;
  clearError: () => void;
};

const ErrorDetailsContext = createContext<ErrorDetails | undefined>(undefined);
const ErrorActionsContext = createContext<ErrorActions | undefined>(undefined);

export const useError = () => {
  const errorDetails = useContext(ErrorDetailsContext);
  const errorActions = useContext(ErrorActionsContext);

  if (!errorDetails || !errorActions) {
    throw new Error('useError must be used within an ErrorProvider');
  }

  return {
    errorDetails,
    ...errorActions,
  };
};

export const useErrorDetails = () => {
  const errorDetails = useContext(ErrorDetailsContext);
  if (!errorDetails) {
    throw new Error('useErrorDetails must be used within an ErrorProvider');
  }
  return errorDetails;
};

export const useErrorActions = () => {
  const actions = useContext(ErrorActionsContext);
  if (!actions) {
    throw new Error('useErrorActions must be used within an ErrorProvider');
  }
  return actions;
};

export const ErrorProvider = ({ children }: { children: ReactNode }) => {
  const [errorDetails, setErrorDetails] = useState<ErrorDetails>({
    status: undefined,
    errorMessage: undefined,
    operationName: undefined,
  });

  const setError = useCallback(
    (status?: number, message?: string, operationNameArg?: string | null) => {
      setErrorDetails({
        status: status,
        errorMessage: message,
        operationName: operationNameArg ?? undefined,
      });
    },
    []
  );

  const clearError = useCallback(() => {
    setErrorDetails({
      status: undefined,
      errorMessage: undefined,
      operationName: undefined,
    });
  }, []);

  const actionsValue = useMemo(
    () => ({
      setError,
      clearError,
    }),
    [setError, clearError]
  );

  return (
    <ErrorActionsContext.Provider value={actionsValue}>
      <ErrorDetailsContext.Provider value={errorDetails}>{children}</ErrorDetailsContext.Provider>
    </ErrorActionsContext.Provider>
  );
};
